// Reusable leaderboard island rendered with TanStack Table. Given a list of
// PerformerRow and the active horizon (`gainKey`), it lays out rank / asset /
// price / cumulative-gain / sparkline columns. The gain cell is colored by the
// sign of the return (green up, red down); the `theme` prop only tints the
// card header (emerald for "gainers", rose for "losers").
//
// Clicking a row teleports (client-side, zero network) to the stock deep-dive
// Info page -- the tracked universe is US actives + XETRA equities.

import { useMemo } from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';

import type { GainKey, PerformerRow } from '../api/useTopPerformers';
import { formatMoney } from '../../../shared/utils/currency';
import { PerformerSparkline } from './PerformerSparkline';

interface PerformersTableProps {
    title: string;
    items: PerformerRow[];
    gainKey: GainKey;
    theme: 'buy' | 'sell';
}

const formatPct = (value: number | null): string =>
    value === null || value === undefined
        ? '—'
        : `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;

// Currency-aware price: EU (XETRA/Vienna) rows render `€`, US rows `$`. Big
// numbers drop the cents to stay dense, mirroring the old formatting.
const formatPrice = (row: PerformerRow): string =>
    formatMoney(
        row.current_price,
        {
            explicitCurrency: row.currency,
            symbol: row.symbol,
            market: row.market,
        },
        {
            minimumFractionDigits: row.current_price >= 1000 ? 0 : 2,
            maximumFractionDigits: row.current_price >= 1000 ? 0 : 2,
        },
    );

const routeForSymbol = (symbol: string): string =>
    `/dashboard/stock-info-page?symbol=${encodeURIComponent(symbol)}`;

const columnHelper = createColumnHelper<PerformerRow>();

export const PerformersTable = ({
    title,
    items,
    gainKey,
    theme,
}: PerformersTableProps) => {
    const navigate = useNavigate();
    const isBuy = theme === 'buy';

    const bgHeader = isBuy
        ? 'bg-emerald-50 dark:bg-emerald-900/10'
        : 'bg-rose-50 dark:bg-rose-900/10';
    const textColor = isBuy
        ? 'text-emerald-700 dark:text-emerald-400'
        : 'text-rose-700 dark:text-rose-400';
    const borderColor = isBuy
        ? 'border-emerald-200 dark:border-emerald-800'
        : 'border-rose-200 dark:border-rose-800';

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'rank',
                header: '#',
                cell: (info) => (
                    <span className="text-xs font-bold text-slate-400 dark:text-slate-500">
                        {info.row.index + 1}
                    </span>
                ),
            }),
            columnHelper.accessor('symbol', {
                header: 'Asset',
                cell: (info) => {
                    const row = info.row.original;
                    return (
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <a
                                    href={routeForSymbol(row.symbol)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-bold text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    {row.symbol}
                                </a>
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5">
                                    {row.market}
                                </span>
                            </div>
                            {row.name && (
                                <span className="text-xs text-slate-500 truncate max-w-[180px]">
                                    {row.name}
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('current_price', {
                header: () => <div className="text-right">Price</div>,
                cell: (info) => (
                    <div className="text-right font-mono font-medium text-slate-900 dark:text-white">
                        {formatPrice(info.row.original)}
                    </div>
                ),
            }),
            columnHelper.accessor((row) => row[gainKey], {
                id: 'gain',
                header: () => <div className="text-right">Return</div>,
                cell: (info) => {
                    const val = info.getValue() as number | null;
                    const up = (val ?? 0) >= 0;
                    return (
                        <div
                            className={`text-right font-mono font-bold ${
                                val === null
                                    ? 'text-slate-400'
                                    : up
                                      ? 'text-emerald-600 dark:text-emerald-400'
                                      : 'text-rose-600 dark:text-rose-400'
                            }`}
                        >
                            {formatPct(val)}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('sparkline', {
                header: () => <div className="text-center">Trend</div>,
                cell: (info) => (
                    <div className="flex justify-center">
                        <PerformerSparkline values={info.getValue()} />
                    </div>
                ),
            }),
        ],
        [gainKey],
    );

    const table = useReactTable({
        data: items,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div
            className={`flex flex-col h-full rounded-xl border ${borderColor} bg-white dark:bg-slate-900 shadow-sm overflow-hidden`}
        >
            <div
                className={`px-5 py-4 border-b ${borderColor} ${bgHeader} flex items-center justify-between`}
            >
                <h3
                    className={`text-lg font-black tracking-tight ${textColor}`}
                >
                    {title}
                </h3>
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest bg-white dark:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 shadow-sm">
                    {isBuy ? 'Cumulative Gain' : 'Cumulative Loss'}
                </span>
            </div>

            <div className="flex-1 overflow-x-auto">
                {items.length === 0 ? (
                    <div className="h-48 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm p-6 text-center">
                        No qualifying movers for this horizon.
                    </div>
                ) : (
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-200 dark:border-slate-700">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} className="px-4 py-3">
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext(),
                                                  )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    onClick={() =>
                                        navigate(
                                            routeForSymbol(row.original.symbol),
                                        )
                                    }
                                    className="group cursor-pointer bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-all"
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-4 py-4">
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default PerformersTable;
